interface User {
  id: string
  name: string
  account: string
  type: 'local' | 'ldap' | 'oidc'
  platform_role: 'owner' | 'admin' | 'auditor' | 'security' | 'user'
  contract_agreement: boolean | null
  totp_enabled: boolean
  last_login_at?: string
  created_at: string
  updated_at: string
}

export interface RequestPayloadCreateUser {
  account: string
  name: string
  password: string
  role: string
}

export interface RequestPayloadUpdateRole {
  role: string
  user_id: string
}

export interface RequestPayloadCreateProject {
  name: string
  description: string
  owner_id: string
}

export interface Request {
  id: string
  requestor_id: string
  approver_id?: string
  type: 'create_user' | 'update_platform_role' | 'create_project'
  payload: string
  status: 'pending' | 'approved' | 'rejected'
  reason?: string
  created_at: string
  processed_at?: string
  requestor: User
  approver?: User
}

export interface RequestsResponse {
  limit: number
  offset: number
  requests: Request[]
}

class RequestService {
  private baseUrl = '/api/v2/requests'

  async getPendingRequests(offset: number = 0, limit: number = 100): Promise<RequestsResponse> {
    const response = await fetch(`${this.baseUrl}/platform/pending?offset=${offset}&limit=${limit}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(errorText || 'Failed to fetch pending requests')
    }

    return response.json()
  }

  async getApprovedRequests(offset: number = 0, limit: number = 100): Promise<RequestsResponse> {
    const response = await fetch(`${this.baseUrl}/platform/approved?offset=${offset}&limit=${limit}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(errorText || 'Failed to fetch approved requests')
    }

    return response.json()
  }

  async getRejectedRequests(offset: number = 0, limit: number = 100): Promise<RequestsResponse> {
    const response = await fetch(`${this.baseUrl}/platform/rejected?offset=${offset}&limit=${limit}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(errorText || 'Failed to fetch rejected requests')
    }

    return response.json()
  }

  async getMyRequests(offset: number = 0, limit: number = 100): Promise<RequestsResponse> {
    const response = await fetch(`${this.baseUrl}/my?offset=${offset}&limit=${limit}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(errorText || 'Failed to fetch my requests')
    }

    return response.json()
  }

  async approveRequest(requestId: string, reason?: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${requestId}/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: reason ? JSON.stringify({ reason }) : undefined,
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(errorText || 'Failed to approve request')
    }
  }

  async rejectRequest(requestId: string, reason?: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/${requestId}/reject`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: reason ? JSON.stringify({ reason }) : undefined,
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(errorText || 'Failed to reject request')
    }
  }
}

export const requestService = new RequestService()
